/**
 * Admin agent API client.
 *
 * The chat endpoint returns Server-Sent Events, which `fetch` exposes as a byte
 * stream rather than an `EventSource` (EventSource cannot POST or send the
 * auth header). Frames are parsed here and delivered as typed callbacks.
 */

import { config } from '../config';

const BASE = `${config.api.baseUrl}/agent`;

export type AgentEventType =
  | 'message.started'
  | 'message.delta'
  | 'message.completed'
  | 'plan'
  | 'reasoning'
  | 'tool.started'
  | 'tool.completed'
  | 'tool.failed'
  | 'job.started'
  | 'job.progress'
  | 'approval.required'
  | 'approval.resolved'
  | 'usage'
  | 'trace'
  | 'heartbeat'
  | 'error'
  | 'done';

export interface AgentEvent {
  type: AgentEventType;
  seq: number;
  [key: string]: unknown;
}

export interface ConversationSummary {
  conversationId: string;
  title: string;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  cumulativeTokens?: number;
  cumulativeCost?: number;
}

export interface TranscriptMessage {
  messageId: string;
  sequence: number;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  createdAt: string;
  activity?: Array<{ summary?: string; toolName?: string }>;
  usage?: { inputTokens?: number; outputTokens?: number; reasoningTokens?: number } | null;
  model?: string | null;
  reasoningEffort?: string | null;
}

export interface AgentModel {
  model: string;
  label: string;
  supportsReasoningEffort: boolean;
  reasoningEfforts: string[];
  defaultReasoningEffort: string | null;
  isDefault: boolean;
}

export interface ApprovalSummary {
  approvalId: string;
  conversationId: string;
  operationType: string;
  lifecycleAction: string | null;
  toolName: string;
  status: string;
  proposalHash: string;
  createdAt: string;
  expiresAt: string;
  irreversible: boolean;
  targets: Array<Record<string, unknown>>;
  affectedCounts: Record<string, number> | null;
  cascadeImpact: Record<string, number> | null;
  beforeValues: unknown;
  afterValues: unknown;
  warnings: string[];
  traceId: string | null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    ...init,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.message || `Request failed (${response.status})`);
    (error as Error & { code?: string; traceId?: string }).code = body.error;
    (error as Error & { code?: string; traceId?: string }).traceId = body.traceId;
    throw error;
  }
  return body as T;
}

export function listConversations(includeArchived = false) {
  return request<{ conversations: ConversationSummary[] }>(
    `/conversations?includeArchived=${includeArchived}`
  );
}

export function listModels() {
  return request<{ models: AgentModel[] }>('/models');
}

export function searchConversations(query: string) {
  return request<{ results: Array<ConversationSummary & { snippet: string }> }>(
    `/conversations?search=${encodeURIComponent(query)}`
  );
}

export function getTranscript(conversationId: string) {
  return request<{ conversation: ConversationSummary; messages: TranscriptMessage[] }>(
    `/conversations/${conversationId}`
  );
}

export function createConversation(title?: string) {
  return request<{ conversation: ConversationSummary }>('/conversations', {
    method: 'POST',
    body: JSON.stringify({ title }),
  });
}

export function renameConversation(conversationId: string, title: string) {
  return request<{ conversation: ConversationSummary }>(`/conversations/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}

export function archiveConversation(conversationId: string) {
  return request<{ conversation: ConversationSummary }>(`/conversations/${conversationId}`, {
    method: 'DELETE',
  });
}

export function listApprovals() {
  return request<{ approvals: ApprovalSummary[] }>('/approvals');
}

export function respondToApproval(
  approvalId: string,
  decision: 'approved' | 'rejected' | 'canceled',
  options: { excludedRowIds?: string[]; rejectionReason?: string } = {}
) {
  return request<{ approval: ApprovalSummary }>(`/approvals/${approvalId}`, {
    method: 'POST',
    body: JSON.stringify({ decision, ...options }),
  });
}

/**
 * Parse an SSE byte stream into frames.
 * Exported for tests; the buffer must survive chunk boundaries that split a frame.
 */
export function createSseParser(onEvent: (event: AgentEvent) => void) {
  let buffer = '';
  return (chunk: string) => {
    buffer += chunk;
    const blocks = buffer.split('\n\n');
    // The trailing element is an incomplete frame; keep it for the next chunk.
    buffer = blocks.pop() ?? '';

    for (const block of blocks) {
      if (!block.trim() || block.startsWith(':')) continue;
      let type: string | null = null;
      let data: string | null = null;
      for (const line of block.split('\n')) {
        if (line.startsWith('event: ')) type = line.slice(7);
        else if (line.startsWith('data: ')) data = line.slice(6);
      }
      if (!type || !data) continue;
      try {
        onEvent({ type: type as AgentEventType, ...JSON.parse(data) });
      } catch {
        // A malformed frame is dropped rather than breaking the stream.
      }
    }
  };
}

/**
 * Send a prompt and stream the response.
 * Returns an abort function so the user can cancel an in-flight turn.
 */
export function sendMessage(
  input: {
    conversationId?: string;
    prompt: string;
    lastEventId?: number;
    model?: string;
    reasoningEffort?: string;
  },
  onEvent: (event: AgentEvent) => void
): { done: Promise<void>; abort: () => void } {
  const controller = new AbortController();

  const done = (async () => {
    const response = await fetch(`${BASE}/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(input.lastEventId ? { 'Last-Event-ID': String(input.lastEventId) } : {}),
      },
      body: JSON.stringify({
        conversationId: input.conversationId,
        prompt: input.prompt,
        model: input.model,
        reasoningEffort: input.reasoningEffort,
      }),
      signal: controller.signal,
    });

    if (!response.body) {
      onEvent({ type: 'error', seq: 0, error: 'STREAM_UNAVAILABLE', message: 'No response stream.' });
      return;
    }

    const parse = createSseParser(onEvent);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      for (;;) {
        const { done: finished, value } = await reader.read();
        if (finished) break;
        parse(decoder.decode(value, { stream: true }));
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') throw error;
    }
  })();

  return { done, abort: () => controller.abort() };
}

export interface UploadPreview {
  mapping: Record<string, string>;
  unmappedHeaders: string[];
  recordCount: number;
  truncated: boolean;
  objectTypeCounts: Record<string, number>;
  isMixed: boolean;
  rows: Array<{
    row: number;
    objectType: string;
    typeConfidence: number;
    missingRequiredFields: string[];
    normalized: Record<string, unknown>;
  }>;
}

export interface JobProgress {
  jobId: string;
  status: string;
  totalRows: number;
  processedRows: number;
  matchedRows: number;
  unmatchedRows: number;
  failedRows: number;
  percentComplete: number;
  canceled: boolean;
}

/** Uploads are small enough (10 MB cap) to send as base64 in a JSON body. */
export async function uploadFile(file: File, conversationId?: string) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // Chunked to avoid blowing the argument limit on a 10 MB file.
  for (let index = 0; index < bytes.length; index += 8192) {
    const chunk = bytes.subarray(index, index + 8192);
    binary += String.fromCharCode.apply(null, Array.from(chunk) as number[]);
  }

  return request<{ uploadId: string; fileHash: string; preview: UploadPreview }>('/uploads', {
    method: 'POST',
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type || 'text/plain',
      contentBase64: btoa(binary),
      conversationId,
    }),
  });
}

export function startMatchJob(
  uploadId: string,
  options: { conversationId?: string; objectType?: string; mapping?: Record<string, string> } = {}
) {
  return request<{ job: JobProgress & { chunkCount: number } }>(`/uploads/${uploadId}/match`, {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

export function getJobProgress(jobId: string) {
  return request<{ progress: JobProgress }>(`/jobs/${jobId}`);
}

export function getJobResults(jobId: string) {
  return request<{ progress: JobProgress; rows: Array<Record<string, unknown>> }>(
    `/jobs/${jobId}/results`
  );
}

export function cancelJob(jobId: string) {
  return request<{ progress: JobProgress }>(`/jobs/${jobId}`, { method: 'DELETE' });
}
